import { FormEvent, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { createLead, LeadPayload } from '../services/leads';
import { trackLeadSubmission } from '../utils/tracking';

interface FormValues {
  name: string;
  email: string;
  role: string;
  ordine: string;
  school: string;
  location: string;
  phone: string;
  size: string;
  interests: string[];
  message: string;
  consentPrivacy: boolean;
  consentMarketing: boolean;
}

const INTEREST_KEYS = [
  'aiLab',
  'pcto',
  'teachers',
  'stem',
  'cyber',
  'infrastructure',
] as const;

type InterestKey = (typeof INTEREST_KEYS)[number];

export const LeadForm = () => {
  const { t, i18n } = useTranslation();
  const [selectedInterests, setSelectedInterests] = useState<InterestKey[]>([]);
  const [formData, setFormData] = useState<Partial<FormValues>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleInterest = (interest: InterestKey) => {
    setSelectedInterests((prev) =>
      prev.includes(interest) ? prev.filter((i) => i !== interest) : [...prev, interest]
    );
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    if (!formData.consentPrivacy) {
      alert(t('form.alerts.privacy'));
      return;
    }

    const entries = Object.fromEntries(data.entries()) as Record<string, FormDataEntryValue>;
    const valueOrEmpty = (key: keyof FormValues) => entries[key]?.toString().trim() ?? '';
    const optionalValue = (key: keyof FormValues) => {
      const value = entries[key];
      return value ? value.toString().trim() : undefined;
    };

    const payload: LeadPayload = {
      name: valueOrEmpty('name'),
      email: valueOrEmpty('email'),
      role: valueOrEmpty('role'),
      school: valueOrEmpty('school'),
      interests: selectedInterests,
      interestLabels: selectedInterests.map((interest) => t(`form.interestOptions.${interest}`)),
      consentPrivacy: Boolean(formData.consentPrivacy),
      consentMarketing: Boolean(formData.consentMarketing),
      locale: i18n.language,
    };

    const ordine = optionalValue('ordine');
    if (ordine) {
      payload.ordine = ordine;
    }

    const location = optionalValue('location');
    if (location) {
      payload.location = location;
    }

    const phone = optionalValue('phone');
    if (phone) {
      payload.phone = phone;
    }

    const size = optionalValue('size');
    if (size) {
      payload.size = size;
    }

    const message = optionalValue('message');
    if (message) {
      payload.message = message;
    }

    try {
      setIsSubmitting(true);
      await createLead(payload);
      trackLeadSubmission({ email: payload.email, name: payload.name });
      alert(t('form.alerts.success'));
      form.reset();
      setSelectedInterests([]);
      setFormData({});
    } catch (error) {
      console.error('Lead form submission failed', error);
      alert(t('form.alerts.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="lg:pl-6" id="contatti">
      <div
        className="rounded-3xl border border-regai shadow-lg bg-regai-surface p-6 sticky top-24"
        id="form"
      >
        <h2 className="text-xl font-bold">{t('form.title')}</h2>
        <p className="text-sm text-regai-muted mt-1">{t('form.subtitle')}</p>

        <form onSubmit={handleSubmit} className="mt-5 grid grid-cols-1 gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium" htmlFor="name">
                {t('form.name')}
              </label>
              <input
                id="name"
                name="name"
                required
                className="mt-1 w-full rounded-xl border border-regai bg-regai-surface px-3 py-2"
                placeholder={t('form.placeholders.name')}
              />
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="email">
                {t('form.email')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="mt-1 w-full rounded-xl border border-regai bg-regai-surface px-3 py-2"
                placeholder={t('form.placeholders.email')}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium" htmlFor="role">
                {t('form.role')}
              </label>
              <select
                id="role"
                name="role"
                required
                className="mt-1 w-full rounded-xl border border-regai bg-regai-surface px-3 py-2"
              >
                <option value="principal">{t('form.roles.principal')}</option>
                <option value="dsga">{t('form.roles.dsga')}</option>
                <option value="animatore">{t('form.roles.animatore')}</option>
                <option value="teacher">{t('form.roles.teacher')}</option>
                <option value="pcto">{t('form.roles.pcto')}</option>
                <option value="it">{t('form.roles.it')}</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="ordine">
                {t('form.order.label')}
              </label>
              <select
                id="ordine"
                name="ordine"
                className="mt-1 w-full rounded-xl border border-regai bg-regai-surface px-3 py-2"
              >
                <option value="secondary">{t('form.order.options.secondary')}</option>
                <option value="primary">{t('form.order.options.primary')}</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium" htmlFor="school">
                {t('form.school')}
              </label>
              <input
                id="school"
                name="school"
                required
                className="mt-1 w-full rounded-xl border border-regai bg-regai-surface px-3 py-2"
                placeholder={t('form.placeholders.school')}
              />
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="location">
                {t('form.location')}
              </label>
              <input
                id="location"
                name="location"
                className="mt-1 w-full rounded-xl border border-regai bg-regai-surface px-3 py-2"
                placeholder={t('form.placeholders.location')}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium" htmlFor="phone">
                {t('form.phone')}
              </label>
              <input
                id="phone"
                name="phone"
                className="mt-1 w-full rounded-xl border border-regai bg-regai-surface px-3 py-2"
                placeholder={t('form.placeholders.phone')}
              />
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="size">
                {t('form.size')}
              </label>
              <select
                id="size"
                name="size"
                className="mt-1 w-full rounded-xl border border-regai bg-regai-surface px-3 py-2"
              >
                <option value="small">{t('form.sizeOptions.small')}</option>
                <option value="medium">{t('form.sizeOptions.medium')}</option>
                <option value="large">{t('form.sizeOptions.large')}</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">{t('form.interests')}</label>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {INTEREST_KEYS.map((interest) => (
                <button
                  key={interest}
                  type="button"
                  onClick={() => toggleInterest(interest)}
                  className={`text-left rounded-xl border px-3 py-2 text-sm transition-colors ${
                    selectedInterests.includes(interest)
                      ? 'bg-regai text-white border-regai-primary'
                      : 'bg-regai-surface border-regai hover:bg-regai-surface-alt/80 dark:bg-[rgba(11,16,32,0.95)] dark:hover:bg-[rgba(20,27,51,0.85)]'
                  }`}
                  aria-pressed={selectedInterests.includes(interest)}
                >
                  {t(`form.interestOptions.${interest}` as const)}
                </button>
              ))}
            </div>

            <label htmlFor="message" className="text-sm font-medium">
              {t('form.message')}
            </label>
            <textarea
              id="message"
              name="message"
              rows={4}
              className="mt-1 w-full rounded-xl border border-regai bg-regai-surface px-3 py-2"
              placeholder={t('form.placeholders.message')}
            />
          </div>

          <div className="space-y-2 text-sm">
            <label className="flex items-start gap-3">
              <input
                id="consentPrivacy"
                type="checkbox"
                className="mt-1"
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, consentPrivacy: e.target.checked }))
                }
                required
              />
              <span id="privacy">
                <Trans
                  i18nKey="form.privacy"
                  components={{ 1: <a href="#privacy" className="text-regai-strong underline" /> }}
                />
              </span>
            </label>
            <label className="flex items-start gap-3 text-regai-muted">
              <input
                id="consentMarketing"
                type="checkbox"
                className="mt-1"
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, consentMarketing: e.target.checked }))
                }
              />
              <span>{t('form.marketing')}</span>
            </label>
          </div>

          <button
            type="submit"
            className={`btn btn-primary ${isSubmitting ? 'opacity-70 cursor-wait' : ''}`}
            disabled={isSubmitting}
          >
            {t('form.submit')}
          </button>
          <p className="text-xs text-regai-muted mt-2">{t('form.footer')}</p>
        </form>
      </div>
    </div>
  );
};
